import React, { useEffect, useState } from "react";
import "./ModalManager.css";
import toast from "react-hot-toast";
import { ImCancelCircle } from "react-icons/im";
import Select, { components } from "react-select";

import * as FaIcons from "react-icons/fa";
import * as MdIcons from "react-icons/md";
import * as AiIcons from "react-icons/ai";

import {
  getParentMenus,
  getAllApplication,
  getRolesForApplication,
} from "../../../../api/ApiConfig";

/* ----------------------------- ICON SETUP ----------------------------- */

const allIcons = { ...FaIcons, ...MdIcons, ...AiIcons };

const iconOptions = Object.keys(allIcons).map((iconName) => ({
  label: iconName,
  value: iconName,
  icon: allIcons[iconName],
}));

/* ----------------------------- ROLE SELECT STYLES (UNIQUE) ----------------------------- */

const roleSelectStyles = {
  control: (base, state) => ({
    ...base,
    backgroundColor: "#ffffff",
    borderColor: state.isFocused ? "#6c63ff" : "#d0d0d0",
    boxShadow: state.isFocused ? "0 0 0 1px #6c63ff" : "none",

    cursor: "pointer",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    borderRadius: "8px",
   
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#f0f1ff"
      : state.isFocused
      ? "#f7f7f7"
      : "#ffffff",
    color: "#333",
    cursor: "pointer",
   
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#eef0ff",
    borderRadius: "6px",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#3f3d9e",
    fontWeight: 500,
  }),
  multiValueRemove: (base) => ({
    ...base,
    ":hover": {
      backgroundColor: "#d8dbff",
      color: "#2f2ca3",
    },
  }),
};

/* ----------------------------- ROLE CHECKBOX OPTION ----------------------------- */

const RoleCheckboxOption = (props) => {
  return (
    <components.Option {...props}>
      <div className="role-multiselect-option">
        <input
          type="checkbox"
          checked={props.isSelected}
          readOnly
          className="role-multiselect-checkbox"
        />
        <span className="role-multiselect-label">{props.label}</span>
      </div>
    </components.Option>
  );
};

/* ----------------------------- MAIN COMPONENT ----------------------------- */

const CreateMenuModal = ({ onClose, onSave }) => {
  const [label, setLabel] = useState("");
  const [url, setUrl] = useState("");
  const [selectedIcon, setSelectedIcon] = useState(null);
  const [parentId, setParentId] = useState("");

  const [parentMenus, setParentMenus] = useState([]);
  const [applications, setApplications] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState(null);

  const [roles, setRoles] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);

  /* ----------------------------- FETCH DATA ----------------------------- */

  useEffect(() => {
    const fetchData = async () => {
      try {
        const parentResponse = await getParentMenus();
        const applicationResponse = await getAllApplication();

        setParentMenus(parentResponse?.data || []);
        setApplications(applicationResponse || []);
      } catch (error) {
        toast.error(`Error fetching data: ${error.message}`);
      }
    };

    fetchData();
  }, []);

  /* ----------------------------- APP CHANGE ----------------------------- */

  const handleApplicationChange = async (selectedOption) => {
    setSelectedApplication(selectedOption);
    setRoles([]);
    setSelectedRoles([]);

    if (!selectedOption) return;

    try {
      const response = await getRolesForApplication(selectedOption.id);
      setRoles(response?.data || response || []);
    } catch (error) {
      toast.error(`Error fetching roles: ${error.message}`);
    }
  };

  /* ----------------------------- OPTIONS ----------------------------- */

  const applicationOptions = applications.map((app) => ({
    label: app.application_name,
    value: app.application_name,
    id: app.application_id,
  }));

  const parentMenuOptions = parentMenus.map((menu) => ({
    label: menu.full_path_label,
    value: menu.slno,
  }));

  const roleOptions = roles.map((role) => ({
    label: role.role_name,
    value: role.role_name,
    id: role.role_id,
  }));

  /* ----------------------------- SAVE ----------------------------- */

  const handleCreate = () => {
    if (!selectedApplication) {
      toast.error("Please select an application");
      return;
    }

    if (!label.trim()) {
      toast.error("Menu label is required");
      return;
    }

    const newMenu = {
      label: label.trim(),
      url: url.trim(),
      icon_name: selectedIcon?.value || null,
      parent_id: parentId || null,
      application_name: selectedApplication.value,
      allowed_roles: selectedRoles.map((r) => r.value),
      display_order: 0,
    };

    onSave(newMenu);
    onClose();
  };

  /* ----------------------------- UI ----------------------------- */

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header">
          <h3>Create New Menu Link</h3>
          <button onClick={onClose}>
            <ImCancelCircle />
          </button>
        </div>

        <div className="modal-content">
          <div className="form-group">
            <label>Application Name</label>
            <Select
              options={applicationOptions}
              value={selectedApplication}
              onChange={handleApplicationChange}
              isClearable
            />
          </div>

          {/* ✅ ROLE MULTI SELECT */}
          <div className="form-group">
            <label>Allowed Roles</label>
            <Select
              classNamePrefix="role-multiselect"
              styles={roleSelectStyles}
              options={roleOptions}
              value={selectedRoles}
              onChange={setSelectedRoles}
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              components={{ Option: RoleCheckboxOption }}
              isDisabled={!selectedApplication}
              placeholder="Select roles..."
            />
          </div>

          <div className="form-group">
            <label>Parent Menu</label>
            <Select
              options={parentMenuOptions}
              onChange={(o) => setParentId(o?.value || "")}
              isClearable
            />
          </div>

          <div className="form-group">
            <label>Label</label>
            <input value={label} onChange={(e) => setLabel(e.target.value)} />
          </div>

          <div className="form-group">
            <label>URL</label>
            <input value={url} onChange={(e) => setUrl(e.target.value)} />
          </div>

          <div className="form-group">
            <label>Icon</label>
            <Select
              options={iconOptions}
              value={selectedIcon}
              onChange={setSelectedIcon}
              isClearable
              isDisabled={!!parentId}
              formatOptionLabel={(option) => (
                <div style={{ display: "flex", gap: 8 }}>
                  {React.createElement(option.icon, { size: 18 })}
                  {option.label}
                </div>
              )}
            />
          </div>
        </div>

        <div className="modal-footer">
          <button className="view-btn" onClick={handleCreate}>
            Create
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateMenuModal;